import { Collapse } from "@mui/material";
import ButtonCollapse from "components/shared/ButtonCollapse";
import TextBtnDropdown from "components/shared/TextBtnDropdown";
import { formatLocaleDateString } from "helpers/utils";
import type { Dispatch, SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import type { TermTemplate } from "store/termTemplatesStore";

import variables from "../../../assets/styles/_variables.module.scss";
import styles from "./style.module.scss";
import TemplateDetails from "./TemplateDetails";

interface ITemplaceBlock {
    /**
     * Term template.
     */
    template: TermTemplate;
    /**
     * Is the collapse open.
     */
    isOpen: boolean;
    /**
     * Sets chosenTemplate to the current template.
     */
    setChosenTemplate: Dispatch<SetStateAction<TermTemplate | undefined>>;
    /**
     * Triggers deletion of template.
     */
    handleDelete: () => void;
    /**
     * Triggers opening form for edit.
     * @param template Template that is being modified.
     */
    handleEdit: (template: TermTemplate) => void;
}

// In Term templates settings, the block for a template (clickable div that opens details):
const TemplateBlock = ({ template, isOpen, setChosenTemplate, handleDelete, handleEdit }: ITemplaceBlock) => {
    const [t] = useTranslation();
    const handleToggleBlock = () => {
        if (isOpen) {
            setChosenTemplate(undefined);
        } else {
            setChosenTemplate(template);
        }
    };

    const settingOptions = [
        { id: 1, name: t("TermTemplates.Edit"), action: () => handleEdit(template) },
        { id: 2, name: t("TermTemplates.Delete"), action: () => handleDelete() },
    ];

    return (
        <div className={styles["block-wrapper"]}>
            <div
                className={styles["term-template-block"]}
                onClick={handleToggleBlock}
                style={{ background: isOpen ? "#f6f6f6" : "" }}
            >
                <div className={styles["header-left"]}>
                    <span className={styles["template-name"]}>{template.name}</span>
                    <span className={styles["template-details"]}>
                        {t("TermTemplates.LastChange", { date: formatLocaleDateString({ date: template.modifiedAt }) })}
                    </span>
                </div>
                <div className={styles["header-right"]}>
                    {isOpen && (
                        <TextBtnDropdown
                            btnText={t("TermTemplates.Settings")}
                            menuOptions={settingOptions}
                            btnStyle={{ color: variables.$info, fontSize: "13px" }}
                        />
                    )}
                    <ButtonCollapse collapse={isOpen} sx={{ color: variables["foreground"] }} />
                </div>
            </div>
            <Collapse in={isOpen} timeout={"auto"}>
                <TemplateDetails templateConfiguration={template?.configuration} handleDelete={handleDelete} />
            </Collapse>
        </div>
    );
};

export default TemplateBlock;
